// mcn-frontend/src/contexts/UserContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { API_ENDPOINTS } from '../config';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUserProfile(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async (token) => {
    try {
      const response = await fetch(API_ENDPOINTS.profile, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
        
        // Set tenant and permissions if available
        if (userData.tenant) {
          setTenant(userData.tenant);
          localStorage.setItem('tenantId', userData.tenant.id);
          localStorage.setItem('tenantName', userData.tenant.name);
        }
        
        if (userData.permissions) {
          setPermissions(userData.permissions);
        }
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('tenantId');
        localStorage.removeItem('tenantName');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = (user) => {
    setCurrentUser(user);
    
    // Set tenant and permissions from login data
    if (user.tenant) {
      setTenant(user.tenant);
      localStorage.setItem('tenantId', user.tenant.id);
      localStorage.setItem('tenantName', user.tenant.name);
    }
    
    if (user.permissions) {
      setPermissions(user.permissions);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tenantName');
    setCurrentUser(null);
    setTenant(null);
    setPermissions([]);
  };

  // NEW: Update user data
  const updateUser = (updates) => {
    setCurrentUser(prev => ({ ...prev, ...updates }));
  };

  // NEW: Permission checking utilities
  const hasPermission = (permission) => {
    if (!currentUser) return false;
    
    // Super admin has all permissions
    if (currentUser.role === 'SUPER_ADMIN') return true;
    
    // Check specific permissions
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    if (!currentUser) return false;
    if (currentUser.role === 'SUPER_ADMIN') return true;
    
    return permissionList.some(permission => permissions.includes(permission));
  };

  const hasAllPermissions = (permissionList) => {
    if (!currentUser) return false;
    if (currentUser.role === 'SUPER_ADMIN') return true;
    
    return permissionList.every(permission => permissions.includes(permission));
  };

  // NEW: Role checking utilities
  const hasRole = (role) => {
    if (!currentUser) return false;
    return currentUser.role === role;
  };

  const hasAnyRole = (roles) => {
    if (!currentUser) return false;
    return roles.includes(currentUser.role);
  };

  // NEW: Get user display name
  const getUserDisplayName = () => {
    if (!currentUser) return '';
    return currentUser.fullName || currentUser.username || currentUser.email.split('@')[0];
  };

  // NEW: Get tenant display name
  const getTenantDisplayName = () => {
    if (!tenant) return 'Choice Network';
    return tenant.displayName || tenant.name;
  };

  // NEW: Check if user is in a specific tenant
  const isInTenant = (tenantId) => {
    if (!tenant) return false;
    return tenant.id === tenantId;
  };

  // NEW: Get role display name
  const getRoleDisplayName = () => {
    if (!currentUser) return '';
    
    const roleDisplayNames = {
      'SUPER_ADMIN': 'Super Administrator',
      'ADMIN': 'Administrator',
      'MANAGER': 'Manager',
      'FINANCE_MANAGER': 'Finance Manager',
      'CONTENT_MANAGER': 'Content Manager',
      'AGGREGATOR': 'Aggregator Partner',
      'CHANNEL_OWNER': 'Channel Owner',
      'CREATOR': 'Content Creator',
      'CONTENT_CREATOR': 'Content Creator',
      'STUDIO': 'Studio',
      'INVESTOR': 'Investor',
      'PREMIUM_INVESTOR': 'Premium Investor',
      'SUPPORT_AGENT': 'Support Agent',
      'ANALYTICS_VIEWER': 'Analytics Viewer',
      'USER': 'User'
    };
    
    return roleDisplayNames[currentUser.role] || currentUser.role;
  };

  // NEW: Get tier information for aggregators
  const getAggregatorTier = () => {
    if (!currentUser || currentUser.role !== 'AGGREGATOR') return null;
    return currentUser.aggregatorProfile?.tier || 'BRONZE';
  };

  // NEW: Get commission rate for aggregators
  const getCommissionRate = () => {
    if (!currentUser || currentUser.role !== 'AGGREGATOR') return 0;
    
    const tierRates = {
      'BRONZE': 5,
      'SILVER': 7,
      'GOLD': 10,
      'PLATINUM': 12
    };
    
    const tier = getAggregatorTier();
    return tierRates[tier] || 5;
  };

  // NEW: Check if user can access a feature
  const canAccessFeature = (feature) => {
    if (!tenant) return false;
    
    // Check if feature is enabled for tenant
    const featureFlags = tenant.featureFlags || {};
    return featureFlags[feature] === true;
  };

  const value = {
    // Keep existing properties for backward compatibility
    currentUser,
    loading,
    login,
    logout,
    isAuthenticated: !!currentUser,
    
    // Add new properties
    user: currentUser, // Alias for consistency
    tenant,
    permissions,
    updateUser,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    getUserDisplayName,
    getTenantDisplayName,
    getRoleDisplayName,
    isInTenant,
    getAggregatorTier,
    getCommissionRate,
    canAccessFeature
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// Export permission constants
export const PERMISSIONS = {
  // User Management
  VIEW_USERS: 'view_users',
  CREATE_USERS: 'create_users',
  EDIT_USERS: 'edit_users',
  DELETE_USERS: 'delete_users',
  
  // Channel Management
  VIEW_CHANNELS: 'view_channels',
  CREATE_CHANNELS: 'create_channels',
  EDIT_CHANNELS: 'edit_channels',
  DELETE_CHANNELS: 'delete_channels',
  
  // Content Management
  VIEW_CONTENT: 'view_content',
  CREATE_CONTENT: 'create_content',
  EDIT_CONTENT: 'edit_content',
  DELETE_CONTENT: 'delete_content',
  APPROVE_CONTENT: 'approve_content',
  REJECT_CONTENT: 'reject_content',
  
  // Analytics
  VIEW_ANALYTICS: 'view_analytics',
  EXPORT_ANALYTICS: 'export_analytics',
  
  // Finance
  VIEW_FINANCE: 'view_finance',
  MANAGE_PAYMENTS: 'manage_payments',
  APPROVE_PAYMENTS: 'approve_payments',
  VIEW_REVENUE: 'view_revenue',
  
  // Investments
  VIEW_INVESTMENTS: 'view_investments',
  CREATE_INVESTMENTS: 'create_investments',
  MANAGE_INVESTMENTS: 'manage_investments',
  
  // Aggregator
  VIEW_AGGREGATORS: 'view_aggregators',
  MANAGE_AGGREGATORS: 'manage_aggregators',
  VIEW_COMMISSIONS: 'view_commissions',
  
  // System
  VIEW_SETTINGS: 'view_settings',
  MANAGE_SETTINGS: 'manage_settings',
  VIEW_AUDIT_LOGS: 'view_audit_logs'
};

// Export role constants
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  MANAGER: 'MANAGER',
  FINANCE_MANAGER: 'FINANCE_MANAGER',
  CONTENT_MANAGER: 'CONTENT_MANAGER',
  AGGREGATOR: 'AGGREGATOR',
  CHANNEL_OWNER: 'CHANNEL_OWNER',
  CREATOR: 'CREATOR',
  STUDIO: 'STUDIO',
  INVESTOR: 'INVESTOR',
  PREMIUM_INVESTOR: 'PREMIUM_INVESTOR',
  SUPPORT_AGENT: 'SUPPORT_AGENT',
  ANALYTICS_VIEWER: 'ANALYTICS_VIEWER',
  USER: 'USER'
};

export default UserContext;